// Reguła 60 (PL1, 24.09): pasek, który się przewija w poziomie, mówi o tym, że coś
// zostało za krawędzią — zanika po tej stronie, po której coś jeszcze jest (obie,
// gdy po obu), a aktywna pozycja stoi w widoku od pierwszej klatki.
//
// Hook tylko LICZY: wystawia `data-fade="start" | "end" | "both"` na elemencie
// przewijanym, a maskę rysuje CSS (`[data-fade]` w `styles/kit.css`). Pasek, który się
// mieści, nie dostaje atrybutu i nie zanika.
//
// Aktywną pozycję (`[aria-current]`) przesuwamy przez `scrollLeft` samego paska, a nie
// przez `scrollIntoView`: ten drugi przewija też OKNO, gdy pasek jest poza ekranem
// (spis FAQ zmienia aktywną kategorię, gdy czytelnik jest w połowie strony — okno
// skoczyłoby wtedy do góry). Rachunek to `inline: 'nearest'` z zapasem na zanikanie,
// żeby przewinięta pozycja nie stała pod maską. Pierwsze ustawienie bez animacji.
import { useEffect, useLayoutEffect, useRef } from 'react'
import type { RefObject } from 'react'

/** Szerokość zanikania = `--sp-8` w `kit.css` (44 px). */
const FADE = 44

function edges(el: HTMLElement): 'start' | 'end' | 'both' | null {
  const max = el.scrollWidth - el.clientWidth
  if (max <= 1) return null
  const start = el.scrollLeft > 1
  const end = el.scrollLeft < max - 1
  return start && end ? 'both' : start ? 'start' : end ? 'end' : null
}

function paint(el: HTMLElement) {
  const v = edges(el)
  if (v) el.dataset.fade = v
  else delete el.dataset.fade
}

/** Przesuwa pasek tak, żeby aktywna pozycja stała w widoku poza maską (`nearest`). */
function reveal(el: HTMLElement, behavior: ScrollBehavior) {
  const item = el.querySelector<HTMLElement>('[aria-current]')
  if (!item || el.scrollWidth <= el.clientWidth) return
  const box = el.getBoundingClientRect()
  const it = item.getBoundingClientRect()
  const left = it.left - box.left - FADE
  const right = it.right - box.right + FADE
  const delta = left < 0 ? left : right > 0 ? Math.min(right, left) : 0
  if (delta !== 0) el.scrollTo?.({ left: el.scrollLeft + delta, behavior })
}

/**
 * @param ref    element przewijany w poziomie
 * @param active klucz aktywnej pozycji; jego zmiana przesuwa ją do widoku
 */
export function useEdgeFade<T extends HTMLElement>(ref: RefObject<T | null>, active?: unknown) {
  const first = useRef(true)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const update = () => paint(el)
    update()
    el.addEventListener('scroll', update, { passive: true })
    // Dopóki czytelnik sam nie ruszył paska, aktywna pozycja wraca do widoku przy każdej
    // zmianie szerokości; potem pasek zostaje tam, gdzie go postawił.
    let touched = false
    const touch = () => { touched = true }
    const MOVES = ['pointerdown', 'wheel', 'touchstart', 'keydown'] as const
    for (const type of MOVES) el.addEventListener(type, touch, { passive: true })
    const settle = () => { if (!touched) reveal(el, 'auto'); paint(el) }
    // Szerokość treści zmienia się bez zmiany ramki paska (krój dochodzi po pierwszej
    // klatce), więc obserwujemy też pozycje. `fonts.ready` nie wystarcza: bywa
    // rozstrzygnięte, zanim krój podmieni szerokości (VB-4, „Report c” na /en/zgloszenie).
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(settle)
    ro?.observe(el)
    for (const child of Array.from(el.children)) ro?.observe(child)
    let live = true
    document.fonts?.ready.then(() => { if (live) settle() }).catch(() => {})
    return () => {
      live = false
      el.removeEventListener('scroll', update)
      for (const type of MOVES) el.removeEventListener(type, touch)
      ro?.disconnect()
    }
  }, [ref])

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const calm = first.current || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    reveal(el, calm ? 'auto' : 'smooth')
    first.current = false
    paint(el)
  }, [ref, active])
}
